import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Bell, LogOut, Menu, X, ShieldAlert, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtDateTime } from "@/lib/apiClient";
import { BrandLogo, themeBg } from "@/components/BrandLogo";
import { managerNav, HUBS } from "@/lib/navConfig";

export function ImpersonationBanner() {
  const { user, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const imp = user?._impersonation;
  if (!imp?.active) return null;
  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 inset-x-0 z-50 bg-amber-500 text-black h-10 flex items-center justify-center gap-4 px-4 text-xs font-bold uppercase tracking-widest"
    >
      <ShieldAlert className="h-4 w-4" />
      <span>Amden Support Mode — impersonating {imp.target_name || imp.target_email}</span>
      <button
        data-testid="stop-impersonation-btn"
        onClick={async () => { await stopImpersonate(); navigate("/amden/companies"); }}
        className="ml-2 bg-black text-amber-400 px-3 py-1 rounded-sm hover:bg-zinc-800 transition-colors"
      >
        Exit
      </button>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.read).length;
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); }
    catch (err) { console.error("Failed to load notifications:", err); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button data-testid="notifications-btn" onClick={() => setOpen(!open)} className="relative p-2 rounded-md hover:bg-zinc-100 transition-colors">
        <Bell className="h-5 w-5 text-zinc-600" />
        {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-500" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-zinc-200 rounded-md shadow-sm z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Notifications</span>
            <button onClick={markAll} className="text-xs text-amber-600 hover:underline">Mark all read</button>
          </div>
          <div className="max-h-80 overflow-y-auto kqm-scroll">
            {items.length === 0 && <div className="p-4 text-sm text-zinc-400">No notifications</div>}
            {items.map((n) => (
              <div key={n.id} className={cn("px-3 py-2 border-b border-zinc-50 text-sm", !n.read && "bg-amber-50/50")}>
                <div className="text-zinc-800">{n.message}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{fmtDateTime(n.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ nav: navProp, children, title }) {
  const { user, company, logout, switchMode } = useAuth();
  const [workBusy, setWorkBusy] = useState(false);
  const toWork = async () => {
    if (workBusy) return;
    setWorkBusy(true);
    if (!user?.employee_id) {
      try { const { data } = await api.post("/auth/my-work/enable"); if (data.action !== "already_linked") toast.success("Your staff profile has been set up"); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setWorkBusy(false); return; }
    }
    await switchMode("employee"); setWorkBusy(false); nav2("/app");
  };
  const isMgr = user?.role === "kqm_manager";
  const nav = isMgr ? managerNav : navProp;
  const home = isMgr ? "/manager" : "/admin";
  const nav2 = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const imp = user?._impersonation?.active;
  const brand = user?.role === "amden_super" && !imp ? { name: "Amden Console" } : company;
  const brandColor = (user?.role === "amden_super" && !imp) ? "#0F172A" : themeBg(company?.primary_color);
  const doLogout = async () => { await logout(); nav2("/login"); };

  const em = { ...(company?.enabled_modules || {}), ...(isMgr ? Object.fromEntries(Object.entries(user?.manager_modules || {}).filter(([, v]) => v === false)) : {}) };
  const enabledNav = nav.filter((item) => em[item.to] !== false);
  const visibleNav = [];
  enabledNav.forEach((item) => {
    const hub = HUBS.find((h) => h.paths.includes(item.to));
    if (!hub) { visibleNav.push(item); return; }
    if (!visibleNav.some((v) => v.hub === hub.label)) visibleNav.push({ to: hub.home || item.to, label: hub.label, icon: hub.icon, hub: hub.label });
  });
  const hubLoc = useLocation();
  const curHub = HUBS.find((h) => h.paths.some((p) => hubLoc.pathname === p || hubLoc.pathname.startsWith(p + "/")) || hubLoc.pathname === h.home);
  const hubTabs = curHub ? [...(curHub.home ? [{ to: curHub.home, label: "Overview" }] : []), ...enabledNav.filter((i) => curHub.paths.includes(i.to))] : [];
  const loc = useLocation();
  useEffect(() => {
    const owner = nav
      .filter((i) => i.to !== home && (loc.pathname === i.to || loc.pathname.startsWith(i.to + "/")))
      .sort((a, b) => b.to.length - a.to.length)[0];
    if (owner && em[owner.to] === false) nav2(home, { replace: true });
  }, [loc.pathname, company]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cn("min-h-screen bg-[#FAFAFA]", imp && "pt-10")}>
      <ImpersonationBanner />
      {/* Sidebar */}
      <aside style={{ backgroundColor: brandColor }} className={cn(
        "fixed z-40 top-0 left-0 h-full w-64 text-zinc-300 flex flex-col transition-transform lg:translate-x-0",
        imp && "top-10 h-[calc(100%-2.5rem)]",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <BrandLogo company={brand} dark className="h-7" />
        </div>
        <nav className="flex-1 overflow-y-auto kqm-scroll py-3">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-5 py-2.5 text-sm font-medium transition-colors border-l-2",
                isActive ? "bg-white/5 text-white border-amber-500" : "border-transparent hover:bg-white/5 hover:text-white"
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-zinc-400 mb-2 truncate">{user?.name}</div>
          {user?.role === "kqm_admin" && <button data-testid="switch-to-work-btn" onClick={toWork} className="flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors mb-2"><Clock className="h-4 w-4" /> My work</button>}
          <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className={cn("sticky z-30 h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-8", imp ? "top-10" : "top-0")}>
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="font-heading font-bold text-lg text-zinc-900">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider text-zinc-400">
              {user?.role === "amden_super" && !imp ? "Amden Support" : isMgr ? `${brand?.name || "Company"}` : `${brand?.name || "Company"} Admin`}
            </span>
            {isMgr && <span data-testid="manager-role-badge" className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Manager</span>}
            <NotificationBell />
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1600px] min-w-0">
          {hubTabs.length > 1 && <div data-testid="hub-tabs" className="flex gap-1 overflow-x-auto mb-4 border-b border-zinc-200 -mt-2">{hubTabs.map((t) => <NavLink key={t.to} end to={t.to} data-testid={`hub-tab-${t.to.split("/").pop()}`} className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${loc.pathname === t.to ? "border-amber-500 font-semibold text-slate-900" : "border-transparent text-zinc-500 hover:text-slate-900"}`}>{t.label}</NavLink>)}</div>}
          {children}
        </main>
      </div>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}
    </div>
  );
}
